import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { EventPayload } from "../../common/types/Event";
import { RootState } from "./../../app/store";
import {
  addWorkLocation,
  cityAndPostOfficeByZip,
  Contract,
  getWorkLocationWithOrgById,
  IdFilter,
  listWorkLocationsAndContractsByOrgId,
  updateCustomerWorkLocationActiveStatus,
  updateWorkLocation,
  WorkLocationAndCustomerOrgIdFilter,
  workLocationById,
  workLocationsByIds,
  WorkLocationIdFilter,
  workTitlesAutoCompleteQuery,
  WorkLocationsIdsFilter,
  WorkLocationTalentPool,
  getWorkLocationTalentPool,
  AddOrRemovePoolTalents,
  runRemoveTalentsFromPool,
  runAddTalentsToPool,
  WorkLocationPoolFilter,
} from "./customerAPI";
import { UpdateActiveStatus } from "./types/commands";
import { WorkLocationAdded, WorkLocationUpdated } from "./types/events";
import {
  CityAndPostOfficeByZip,
  CityAndPostOfficeByZipResponse,
  CustomerWorkLocationsAndContracts,
  CustomerWorkLocationsAndOrganizations,
  WorkLocation,
  WorkLocationById,
  WorkTitlesAutoCompleteFilters,
  WorkTitlesAutoCompleteResponse,
} from "./types/queries";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";

// TODO: might refactor to not use entity adapters
const customerWorkLocationsAdapter = createEntityAdapter<WorkLocation>();
const customerContractsAdapter = createEntityAdapter<Contract>();

type Entities = {
  ids: string[];
  entities: Record<string, any>;
};

type SliceState = {
  workLocations: Entities;
  contracts: Entities;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const formatWorkLocation = (workLocation: WorkLocation) => {
  const { customerCCs, salesPersonId, salesPersonName, ...rest } = workLocation;

  const customerCCIds = workLocation.customerCCs.map(
    (customerCC: { customerCCId: string; name: string }) =>
      customerCC.customerCCId,
  );

  const formattedWorkLocation = {
    ...rest,
    salesPersonId,
    customerCCIds,
  };

  return formattedWorkLocation;
};

export const fetchWorkLocationsAndContractsByOrgId = createAsyncThunk<
  CustomerWorkLocationsAndContracts,
  IdFilter,
  { state: RootState }
>(
  "customers/fetchCustomerWorkLocations",
  async (filters: IdFilter, { requestId }) => {
    return await listWorkLocationsAndContractsByOrgId(filters, {
      requestId,
    });
  },
);

export const fetchWorkLocationWithOrgById = createAsyncThunk<
  CustomerWorkLocationsAndOrganizations,
  WorkLocationAndCustomerOrgIdFilter,
  { state: RootState }
>(
  "customers/fetchCustomerWorkLocationWithOrgById",
  async (filters: WorkLocationAndCustomerOrgIdFilter, { requestId }) => {
    return await getWorkLocationWithOrgById(filters, {
      requestId,
    });
  },
);
export const updateCustomerWorkLocation = createAsyncThunk<
  EventPayload<WorkLocationUpdated>[],
  WorkLocation,
  { state: RootState }
>(
  "customers/updateWorklocation",
  async (
    workLocation: WorkLocation,
    { requestId },
  ): Promise<EventPayload<WorkLocationUpdated>[]> => {
    const formattedWorkLocation = formatWorkLocation(workLocation);
    return await updateWorkLocation(formattedWorkLocation, { requestId });
  },
);

export const addCustomerWorkLocation = createAsyncThunk<
  EventPayload<WorkLocationAdded>,
  WorkLocation,
  { state: RootState }
>(
  "customers/addWorkLocation",
  async (
    workLocation: WorkLocation,
    { requestId },
  ): Promise<EventPayload<WorkLocationAdded>> => {
    const formattedWorkLocation = formatWorkLocation(workLocation);
    return await addWorkLocation(formattedWorkLocation, { requestId });
  },
);

export const updateWorkLocationActiveStatus = createAsyncThunk<
  EventPayload<{}>,
  UpdateActiveStatus,
  { state: RootState }
>(
  "customers/updateOrderTemplateActiveStatus",
  async (params: UpdateActiveStatus, { requestId }) =>
    await updateCustomerWorkLocationActiveStatus(params, { requestId }),
);

export const getCityAndPostOfficeByZip = createAsyncThunk<
  CityAndPostOfficeByZipResponse,
  CityAndPostOfficeByZip,
  { state: RootState }
>(
  "customers/getCityAndPostOfficeByZip",
  async (params: CityAndPostOfficeByZip, { requestId }) =>
    await cityAndPostOfficeByZip(params, { requestId }),
);

export const workTitlesAutoComplete = createAsyncThunk<
  WorkTitlesAutoCompleteResponse[],
  WorkTitlesAutoCompleteFilters,
  { state: RootState }
>(
  "customers/workTitlesAutoComplete",
  async (params: WorkTitlesAutoCompleteFilters, { requestId }) =>
    await workTitlesAutoCompleteQuery(params, { requestId }),
);

export const fetchWorkLocationById = createAsyncThunk<
  WorkLocationById,
  WorkLocationIdFilter,
  { state: RootState }
>(
  "customers/workLocationById",
  async (params: WorkLocationIdFilter, { requestId }) =>
    await workLocationById(params, { requestId }),
);

export const fetchWorkLocationsByIds = createAsyncThunk<
  WorkLocationById[],
  WorkLocationsIdsFilter,
  { state: RootState }
>(
  "customers/workLocationsByIds",
  async (params: WorkLocationsIdsFilter, { requestId }) =>
    await workLocationsByIds(params, { requestId }),
);

export const fetchWorkLocationTalentPool = createQueryAsyncThunk<
  WorkLocationTalentPool,
  WorkLocationPoolFilter
>("customers/fetchWorkLocationTalentPool", getWorkLocationTalentPool);

export const removeTalentsFromPool = createCommandAsyncThunk<
  AddOrRemovePoolTalents,
  EventPayload<{}>
>("customers/removeTalentsFromPool", runRemoveTalentsFromPool);

export const addTalentsToPool = createCommandAsyncThunk<
  AddOrRemovePoolTalents,
  EventPayload<{}>
>("customers/addTalentsToPool", runAddTalentsToPool);

export const customerWorkLocations = createSlice({
  name: "customers",
  initialState: {
    workLocations: customerWorkLocationsAdapter.getInitialState({}),
    contracts: customerContractsAdapter.getInitialState({}),
    status: "idle",
  } as SliceState,
  reducers: {
    setWorkLocationsOrderingUsers: (
      state,
      action: PayloadAction<{
        customerUserId: string;
        addTo: string[];
        removeFrom: string[];
        events: any[];
      }>,
    ) => {
      if (state.workLocations) {
        action.payload.addTo.forEach((workLocationId) => {
          const selectWorkLocationById =
            customerWorkLocationsAdapter.getSelectors().selectById;

          const currentWorkLocation = selectWorkLocationById(
            state.workLocations,
            workLocationId,
          );

          // const currentWorkLocation = current(
          //   state.workLocations.entities[workLocationId]
          // );

          const currentVersion =
            action.payload.events.find(
              (workLocation) => workLocation.id === workLocationId,
            )?.version || 0;

          const updatedCustUsersWhoCanOrder =
            currentWorkLocation?.custUsersWhoCanOrder
              ? currentWorkLocation.custUsersWhoCanOrder.concat(
                  action.payload.customerUserId,
                )
              : [action.payload.customerUserId];

          customerWorkLocationsAdapter.updateOne(state.workLocations, {
            id: workLocationId,
            changes: {
              version: currentVersion,
              custUsersWhoCanOrder: updatedCustUsersWhoCanOrder,
            },
          });
        });

        action.payload.removeFrom.forEach((workLocationId) => {
          const currentWorkLocation =
            state.workLocations.entities[workLocationId];

          const currentVersion =
            action.payload.events.find(
              (workLocation) => workLocation.id === workLocationId,
            )?.version || 0;

          const updatedCustWhoCanOrder =
            currentWorkLocation?.custUsersWhoCanOrder.filter(
              (custUserId: string) =>
                custUserId !== action.payload.customerUserId,
            );

          customerWorkLocationsAdapter.updateOne(state.workLocations, {
            id: workLocationId,
            changes: {
              version: currentVersion,
              custUsersWhoCanOrder: updatedCustWhoCanOrder,
            },
          });
        });
      }
    },
    resetCustomerWorkLocations: (state) => {
      state.workLocations = customerWorkLocationsAdapter.getInitialState({});
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCityAndPostOfficeByZip.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(getCityAndPostOfficeByZip.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(getCityAndPostOfficeByZip.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkLocationById.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchWorkLocationById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkLocationById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkLocationsByIds.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchWorkLocationsByIds.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkLocationsByIds.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkLocationWithOrgById.fulfilled, (state, action) => {
      if (action.payload.workLocations[0]) {
        customerWorkLocationsAdapter.setOne(
          state.workLocations,
          action.payload.workLocations[0],
        );
        state.status = "succeeded";
      }
    });
    builder.addCase(fetchWorkLocationWithOrgById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkLocationWithOrgById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      fetchWorkLocationsAndContractsByOrgId.fulfilled,
      (state, action) => {
        customerContractsAdapter.setAll(
          state.contracts,
          action.payload.contracts,
        );
        customerWorkLocationsAdapter.setAll(
          state.workLocations,
          action.payload.workLocations,
        );
        state.status = "succeeded";
      },
    );
    builder.addCase(fetchWorkLocationsAndContractsByOrgId.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkLocationsAndContractsByOrgId.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateCustomerWorkLocation.fulfilled, (state, action) => {
      const id = action.meta.arg.id;
      const { customerCCs, salesPersonName, talentCustomerContactPersons } =
        action.meta.arg;

      const updateWorkLocation = (changes: object) =>
        customerWorkLocationsAdapter.updateOne(state.workLocations, {
          id,
          changes,
        });

      action.payload.forEach(({ name: eventName, version, payload }) => {
        const {
          name,
          descriptionForEezy,
          address,
          salesPersonId,
          descriptionForTalent,
          descriptionForTalentEng,
          info,
          contractId,
          invoiceContactPerson = null,
          invoiceReferences = null,
        } = payload;

        switch (eventName) {
          case "wrlNameChg":
            updateWorkLocation({ name, version });
            break;
          case "wrlDescEezyChg":
            updateWorkLocation({ descriptionForEezy, version });
            break;
          case "wrlSlpChg":
            updateWorkLocation({
              ...{ salesPersonId, salesPersonName },
              version,
            });
            break;
          case "wrlAddressChg":
            updateWorkLocation({ address, version });
            break;
          case "wrlDescTalChg":
            updateWorkLocation({ descriptionForTalent, version });
            break;
          case "wrlDescTalEngChg":
            updateWorkLocation({ descriptionForTalentEng, version });
            break;
          case "wrlCccIdsChg":
            updateWorkLocation({ customerCCs, version });
            break;
          case "wrlInfoChg":
            updateWorkLocation({ info, version });
            break;
          case "wrlCntIdChg":
            updateWorkLocation({ contractId, version });
            break;
          case "wrlTalCusConPersonsChg":
            updateWorkLocation({ talentCustomerContactPersons, version });
            break;
          case "wrlInvRefsDel":
          case "wrlInvRefsChg":
            updateWorkLocation({ invoiceReferences, version });
            break;
          case "wrlInvConPerDel":
          case "wrlInvConPerChg":
            updateWorkLocation({ invoiceContactPerson, version });
            break;
          default:
            break;
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(updateCustomerWorkLocation.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerWorkLocation.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addCustomerWorkLocation.fulfilled, (state, action) => {
      const { id, payload, version } = action.payload;
      const { customerCCs, salesPersonName, talentCustomerContactPersons } =
        action.meta.arg;

      const {
        customerOrgId,
        name,
        descriptionForEezy,
        address,
        salesPersonId,
        descriptionForTalent,
        descriptionForTalentEng,
        info,
        contractId,
        invoiceContactPerson = null,
        invoiceReferences = null,
      } = payload;

      const workLocation = {
        version,
        id,
        customerOrgId,
        name,
        address,
        customerCCs,
        info,
        descriptionForEezy,
        descriptionForTalent,
        descriptionForTalentEng,
        contractId,
        salesPersonId,
        salesPersonName,
        talentCustomerContactPersons,
        invoiceContactPerson,
        invoiceReferences,
        active: true,
      };

      customerWorkLocationsAdapter.addOne(state.workLocations, workLocation);

      state.status = "succeeded";
    });
    builder.addCase(addCustomerWorkLocation.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addCustomerWorkLocation.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateWorkLocationActiveStatus.fulfilled,
      (state, action) => {
        const { id, version, name } = action.payload;

        const active = name === "wrlActivated";
        const changes = { version, active };

        customerWorkLocationsAdapter.updateOne(state.workLocations, {
          id,
          changes,
        });

        state.status = "succeeded";
      },
    );
    builder.addCase(updateWorkLocationActiveStatus.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateWorkLocationActiveStatus.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkLocationTalentPool.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchWorkLocationTalentPool.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkLocationTalentPool.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTalentsFromPool.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(removeTalentsFromPool.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeTalentsFromPool.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addTalentsToPool.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(addTalentsToPool.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addTalentsToPool.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export const {
  selectAll: selectAllCustomerWorkLocations,
  selectById: selectCustomerWorkLocationById,
} = customerWorkLocationsAdapter.getSelectors(
  (state: RootState) => state.customerWorkLocations.workLocations,
);

export const {
  selectAll: selectAllCustomerContracts,
  selectById: selectCustomerContractById,
} = customerContractsAdapter.getSelectors(
  (state: RootState) => state.customerWorkLocations.contracts,
);

export const { setWorkLocationsOrderingUsers, resetCustomerWorkLocations } =
  customerWorkLocations.actions;

export const customerWorkLocationsReducer = customerWorkLocations.reducer;

export const selectActiveWorkLocations = createSelector(
  (state: RootState) => selectAllCustomerWorkLocations(state),
  (workLocations) =>
    workLocations.filter((workLocation) => workLocation.active),
);
